import { useState, type FormEvent } from 'react'
import type { AdminWorkspace, PlanName } from '../../api/types'
import { useGrantCredits, useSetLiveLimit, useSetPlan, useSetWorkspaceStatus } from '../../hooks/useAdmin'
import { Button, ErrorNote } from '../ui'

/** Move a workspace between plans by hand. */
export function PlanSelect({ workspace }: { workspace: AdminWorkspace }) {
  const setPlan = useSetPlan()
  return (
    <>
      <select
        value={workspace.plan}
        disabled={setPlan.isPending}
        aria-label={`Plan for ${workspace.name}`}
        onChange={(event) => setPlan.mutate({ workspaceId: workspace.id, plan: event.target.value as PlanName })}
      >
        <option value="free">Free</option>
        <option value="premium">Premium</option>
      </select>
      {setPlan.error && <ErrorNote error={setPlan.error} />}
    </>
  )
}

/** Stop a workspace making new work, or start it again. Reading and paying
 *  keep working either way, and a broadcast on air ends at the next check. */
export function SuspendControl({ workspace }: { workspace: AdminWorkspace }) {
  const setStatus = useSetWorkspaceStatus()
  const suspended = workspace.status === 'suspended'
  return (
    <>
      <Button
        variant={suspended ? 'secondary' : 'ghost'}
        busy={setStatus.isPending}
        onClick={() => {
          if (suspended) {
            setStatus.mutate({ workspaceId: workspace.id, suspended: false })
            return
          }
          const reason = window.prompt(`Stop ${workspace.name}? It can still be read and paid for, but no new work can be made. Why?`)
          if (reason === null) return
          setStatus.mutate({ workspaceId: workspace.id, suspended: true, reason })
        }}
      >
        {suspended ? 'Start it again' : 'Stop this workspace'}
      </Button>
      {suspended && workspace.suspended_reason && <span className="muted small">{workspace.suspended_reason}</span>}
      {setStatus.error && <ErrorNote error={setStatus.error} />}
    </>
  )
}

/** How many shows the workspace may have live at once: one, or more for a site with several channels. */
export function LiveLimitSelect({ workspace }: { workspace: AdminWorkspace }) {
  const setLimit = useSetLiveLimit()
  return (
    <>
      <select
        value={workspace.max_live_shows ?? 1}
        disabled={setLimit.isPending}
        aria-label={`Shows ${workspace.name} can have live at once`}
        onChange={(event) => {
          const max = Number(event.target.value)
          setLimit.mutate({ workspaceId: workspace.id, max: max === 1 ? null : max })
        }}
      >
        {[1, 2, 3, 4, 5, 10, 20].map((n) => (
          <option key={n} value={n}>{n === 1 ? 'One live show at a time' : `${n} live shows at once`}</option>
        ))}
      </select>
      {setLimit.error && <ErrorNote error={setLimit.error} />}
    </>
  )
}

/** Add credits that never expire, with a reason for the ledger. */
export function GrantForm({ workspaceId }: { workspaceId: string }) {
  const grant = useGrantCredits()
  const [credits, setCredits] = useState('')
  const [note, setNote] = useState('')
  const submit = (event: FormEvent) => {
    event.preventDefault()
    grant.mutate(
      { workspaceId, credits: Number(credits), note: note.trim() },
      {
        onSuccess: () => {
          setCredits('')
          setNote('')
        },
      },
    )
  }
  return (
    <form className="row" onSubmit={submit}>
      <input className="input-narrow" type="number" min={1} max={1000000} value={credits} onChange={(event) => setCredits(event.target.value)} placeholder="Credits" aria-label="Credits" />
      <input className="input-medium" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Why" maxLength={200} aria-label="Reason" />
      <Button type="submit" variant="secondary" busy={grant.isPending} disabled={!credits || !note.trim()}>
        Grant
      </Button>
      {grant.error && <ErrorNote error={grant.error} />}
    </form>
  )
}
